import { from, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';
import { Inject, Injectable, Optional } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpClient } from '@angular/common/http';

import { OAuthService } from 'angular-oauth2-oidc';
import { environment } from '~env/environment';
import { TokenService } from '~core/modules/identity/services/token.service';
import { OAUTH_EVENTS } from '~core/constants/oauth-events.constant';
import { HttpErrorStatuses } from '~shared/modules/error-handler';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { FF_ACCOUNT_INFO_V2 } from '~app/modules/authorization/constants/authorization.constant';
import { ApiConfig, NUMBER_1 } from '~shared/constants';
import { LOGIN_USER_TYPES, METROMARKETS_IMPERSONATE_BUYER, USER_TYPE_KEY } from '../constants/identity.constant';
import { deleteSoftLoginSession, generateIsSilentAuthDisabled$, isSoftLoggedIn } from '~server/utils/auth.utils';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { getCountryDomainCode } from '~server/utils/country-detection.util';

@Injectable()
export class RefreshAccessInterceptor implements HttpInterceptor {
  private isRefreshingToken = false;
  private readonly IDAM_SSO_REALM = environment.IDAMSSO && environment.IDAMSSO().realm;
  private isSilentLoginDisabled$ = generateIsSilentAuthDisabled$(
    this.activeFeaturesConfigCatService,
    this.oauthService
  );
  constructor(
    private oauthService: OAuthService,
    private tokenService: TokenService,
    private activeFeaturesConfigCatService: ActiveFeaturesConfigcatService,
    private httpClient: HttpClient,
    @Optional() @Inject(REQUEST) private request: any
  ) {}

  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error) => {
        let isAuthorizationError = false;

        if (error.status === HttpErrorStatuses.Unauthorized) {
          isAuthorizationError = true;
          if (isSoftLoggedIn()) {
            this.httpClient
              .get(ApiConfig.BUYER_REACT_COOKIE_CLEAR_API_URL())
              .pipe(
                finalize(() => {
                  deleteSoftLoginSession();
                  window.location.reload();
                })
              )
              .subscribe();
          }
        }

        if (
          error.status === HttpErrorStatuses.BadRequest &&
          error.detail &&
          error.detail.toLowerCase().includes('token') &&
          !error.detail.toLowerCase().includes('market')
        ) {
          isAuthorizationError = true;
        }

        if (!isAuthorizationError) {
          return throwError(error);
        }

        if (isAuthorizationError) {
          if (this.isRefreshingToken) {
            return this.oauthService.events.pipe(
              filter((result) => result.type === OAUTH_EVENTS.SILENTLY_REFRESHED),
              take(1),
              switchMap(() => {
                return next.handle(this.tokenService.addAuthorizationHeader(request));
              })
            );
          } else {
            this.isRefreshingToken = true;

            return from(this.silentLoginFlow()).pipe(
              switchMap(() => {
                return next.handle(this.tokenService.addAuthorizationHeader(request));
              }),
              catchError((silentRefreshError) => {
                return throwError(silentRefreshError);
              }),
              finalize(() => {
                this.isRefreshingToken = false;
              })
            );
          }
        }

        return this.tokenService.updateToken().pipe(
          switchMap(() => {
            return next.handle(this.tokenService.addAuthorizationHeader(request));
          })
        );
      })
    );
  }

  private async silentLoginFlow() {
    const isEmployeeLoginEnabled = await this.activeFeaturesConfigCatService
      .isFeatureEnabledReadyForCurrentCountry$(FF_ACCOUNT_INFO_V2)
      .pipe(take(NUMBER_1))
      .toPromise();

    const userType = isEmployeeLoginEnabled ? localStorage.getItem(USER_TYPE_KEY) ?? 'CUST' : 'CUST';

    if (userType === LOGIN_USER_TYPES.EMPLOYEE) {
      this.oauthService.scope = `openid role=${METROMARKETS_IMPERSONATE_BUYER}`;
    }
    const isDisabled = await this.isSilentLoginDisabled$.toPromise();

    let res = null;
    if (!isDisabled) {
      res = await this.oauthService.silentRefresh({
        realm_id: this.IDAM_SSO_REALM,
        country_code: getCountryDomainCode(this.request)?.toUpperCase(),
        user_type: userType,
      });
    }
    return res;
  }
}
